import { AlertTriangle, CheckCircle2, XCircle, Loader2, ShieldAlert } from "lucide-react";

const EP_TONE = { complete: "text-emerald-700", running: "text-blue-700", incomplete: "text-amber-700", failed: "text-rose-700" };
const EP_LABEL = { complete: "Completa", running: "Lendo…", incomplete: "Incompleta", failed: "Falhou" };
const JOB_LABEL = { pending: "Aguardando", running: "Executando", done: "Concluída", failed: "Falhou", interrupted: "Interrompida", error: "Falhou" };

const WriteProgress = ({ p }) => {
  const pct = Math.min(100, Math.round(((p.processed || 0) / p.total) * 100));
  return (
    <div className="mt-2" data-testid="write-progress">
      <div className="h-2 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }}/></div>
      <div className="mt-1 grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-[11px]">
        <span data-testid="write-processed">Processados: <b>{p.processed ?? 0}</b> / {p.total} ({pct}%)</span>
        <span data-testid="write-created">Gravados: <b>{p.created ?? 0}</b></span>
        <span data-testid="write-ignored">Ignorados (já existiam): <b>{p.ignored ?? 0}</b></span>
        <span data-testid="write-errors" className={p.errors ? "text-rose-700" : ""}>Erros: <b>{p.errors ?? 0}</b></span>
      </div>
    </div>
  );
};

export const AnalysisJobState = ({ job }) => {
  if (!job) return null;
  const running = job.status === "running" || job.status === "pending";
  const eps = job.endpoints || {};
  return (
    <div className={`mt-4 rounded-lg border p-3 text-xs ${job.status === "failed" || job.status === "interrupted" ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid="analyze-job-state">
      <div className="flex items-center gap-2 font-semibold">
        {running && <Loader2 className="w-3 h-3 animate-spin"/>}
        Análise {job.job_id?.slice(0, 8)} · <span data-testid="analyze-job-status">{JOB_LABEL[job.status] || job.status}</span>
        {job.progress?.phase && running && <span className="text-slate-500 font-normal">· etapa: {job.progress.phase}</span>}
      </div>
      {Object.keys(eps).length > 0 && (
        <div className="mt-1 font-mono text-[11px] text-slate-700">
          {Object.entries(eps).map(([k, e]) => <div key={k}>{e.endpoint}: {e.records ?? 0} registros · {e.pages ?? 0} págs · {EP_LABEL[e.status] || e.status}</div>)}
        </div>
      )}
      {job.updated_at && <div className="text-slate-500 mt-1">Último checkpoint salvo: {new Date(job.updated_at).toLocaleString("pt-BR")}</div>}
      {["write", "done"].includes(job.progress?.phase) && job.progress.total > 0 && <WriteProgress p={job.progress}/>}
      {job.error && <div className="text-rose-700 font-semibold mt-1" data-testid="analyze-job-error">{job.error.detail}</div>}
    </div>
  );
};

const Validity = ({ pv, validity }) => (
  <div className="space-y-2 mb-4">
    {validity?.migration_allowed ? (
      <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-xs flex gap-2" data-testid="validity-ok"><CheckCircle2 className="w-4 h-4 text-emerald-600"/>Sem inconsistências críticas.</div>
    ) : (
      <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-xs" data-testid="validity-blocked">
        <div className="font-bold text-rose-800 flex items-center gap-1 mb-1"><ShieldAlert className="w-4 h-4"/>Confirmação e migração BLOQUEADAS</div>
        <ul className="list-disc ml-5 text-rose-900 space-y-0.5">{(validity?.blocking_reasons || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
      </div>
    )}
    {(validity?.plan_warnings || []).length > 0 && (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="plan-warnings">
        {validity.plan_warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
      </div>
    )}
    {(pv.warnings || []).length > 0 && (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs" data-testid="validity-warnings">
        <div className="font-bold text-amber-800 flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4"/>Avisos (não bloqueiam)</div>
        <ul className="list-disc ml-5 text-amber-900 space-y-0.5">{pv.warnings.map((w, i) => <li key={i}>{w.message}</li>)}</ul>
      </div>
    )}
  </div>
);

const EndpointsTable = ({ endpoints }) => (
  <div className="rounded-lg border border-slate-200 overflow-x-auto mb-4" data-testid="endpoints-report">
    <table className="w-full text-[11px]">
      <thead className="bg-slate-50 text-slate-600 uppercase text-[10px]"><tr>{["Endpoint", "Situação", "Registros", "Únicos", "Páginas", "Novas tentativas", "Páginas c/ falha", "Tempo"].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
      <tbody>
        {Object.entries(endpoints || {}).map(([k, e]) => (
          <tr key={k} className="border-t border-slate-100" data-testid={`endpoint-row-${k}`}>
            <td className="px-2 py-1 font-mono">{e.endpoint}</td>
            <td className={`px-2 py-1 font-bold ${EP_TONE[e.status] || ""}`}>{e.status === "complete" ? <CheckCircle2 className="inline w-3 h-3 mr-1"/> : <XCircle className="inline w-3 h-3 mr-1"/>}{EP_LABEL[e.status] || e.status}{e.error ? ` — ${e.error}` : ""}</td>
            <td className="px-2 py-1">{e.records}</td><td className="px-2 py-1">{e.unique_ids}</td><td className="px-2 py-1">{e.pages}</td>
            <td className="px-2 py-1">{e.retries}</td><td className="px-2 py-1">{(e.failed_pages || []).length}</td><td className="px-2 py-1">{e.duration_s ?? "—"}s</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const KV = ({ title, data, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="text-xs font-bold uppercase text-slate-600 mb-1">{title}</div>
    <div className="text-[11px] space-y-0.5">
      {Object.entries(data || {}).map(([k, v]) => (typeof v === "object" && v !== null) ? (
        <div key={k}><span className="text-slate-500">{k}:</span><ul className="ml-3">{Object.entries(v).map(([k2, v2]) => <li key={k2}>{k2}: <code>{String(v2)}</code></li>)}</ul></div>
      ) : <div key={k}><span className="text-slate-500">{k}:</span> <code className="font-bold">{String(v)}</code></div>)}
    </div>
  </div>
);

const PendingSamples = ({ samples }) => {
  const rows = Object.entries(samples || {}).filter(([k]) => k.startsWith("pendente")).flatMap(([k, list]) => list.map((r) => ({ tipo: k, ...r })));
  if (!rows.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto mb-4" data-testid="pending-samples">
      <div className="px-3 py-2 bg-slate-50 text-xs font-bold uppercase text-slate-600">Pendentes de revisão (amostra, dados mascarados)</div>
      <table className="w-full text-[11px]">
        <thead className="text-[10px] uppercase text-slate-500"><tr>{["Tipo", "Nome", "CPF final", "Situação", "Financeiro", "Motivo"].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100"><td className="px-2 py-1">{r.tipo}</td><td className="px-2 py-1">{r.nome}</td><td className="px-2 py-1">**{r.cpf_final}</td><td className="px-2 py-1">{r.situacao}</td><td className="px-2 py-1">{r.financeiro}</td><td className="px-2 py-1">{r.motivo}</td></tr>)}</tbody>
      </table>
    </div>
  );
};

export const NextFitAnalysisReport = ({ preview }) => {
  const pv = preview?.preview || {};
  const s = pv.summary || {};
  if (!pv.endpoints) return <div className="text-xs text-amber-700 mb-3" data-testid="legacy-preview">Prévia gerada pela versão antiga (sem contratos/mensalidades). Rode "Analisar" novamente.</div>;
  const d = s.destino || {};
  return (
    <div data-testid="analysis-report">
      <Validity pv={pv} validity={preview.validity}/>
      <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 text-xs mb-4" data-testid="destination-box">
        <div className="font-bold text-blue-900">Destino: {d.unit_name} <code className="font-normal">({d.unit_id})</code></div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-2">
          {[["Novos a importar", d.importar_novos, "new"], ["Reutilizados c/ atualização", d.reutilizar_atualizar, "reuse-upd"], ["Reutilizados sem alteração", d.reutilizar_sem_alteracao, "reuse"], ["Pendentes de revisão", d.pendentes_revisao, "pending"], ["Ignorados sem contrato", d.ignorados_sem_contrato, "no-contract"], ["Ignorados (CPF)", d.ignorados_cpf, "no-cpf"]].map(([l, v, t]) => (
            <div key={l} className="rounded bg-white border border-blue-100 p-2" data-testid={`dest-${t}`}><div className="text-[10px] uppercase text-slate-500">{l}</div><div className="text-lg font-black">{v ?? 0}</div></div>
          ))}
        </div>
        <div className="mt-2 text-[11px]">Gravações previstas: <strong>{d.total_gravacoes_previstas ?? 0}</strong> · conferência de totais: <strong className={d.conferencia_totais_ok ? "text-emerald-700" : "text-rose-700"}>{d.conferencia_totais_ok ? "OK" : "DIVERGENTE"}</strong></div>
        {preview.validity?.plan_summary && (
          <div className="mt-1 text-[11px]" data-testid="plan-summary">Planos: <strong>{preview.validity.plan_summary.aproveitar}</strong> aproveitados · <strong>{preview.validity.plan_summary.criar}</strong> a criar · <strong className="text-amber-700">{preview.validity.plan_summary.revisar}</strong> em revisão · {preview.validity.plan_summary.definido_por_voce} definidos por você · {preview.validity.plan_summary.ignorar} não serão criados (sem alunos)</div>
        )}
      </div>
      <EndpointsTable endpoints={pv.endpoints}/>
      <div className="grid md:grid-cols-2 gap-3 mb-4">
        <KV title="Pessoas / CPF" data={s.pessoas} testid="sum-pessoas"/>
        <KV title="Ignorados sem CPF / CPF inválido" data={s.ignorados_sem_cpf_ou_invalido} testid="sum-sem-cpf"/>
        <KV title="Situação dos alunos (contrato)" data={s["situacao_alunos (por CPF único)"]} testid="sum-situacao"/>
        <KV title="Situação financeira (ContaReceber)" data={{ ...s["situacao_financeira_alunos (por CPF único)"], ...(s.alertas_importacao || {}) }} testid="sum-financeiro"/>
        <KV title="Contratos" data={s.contratos} testid="sum-contratos"/>
        <KV title="Mensalidades" data={s.mensalidades} testid="sum-mensalidades"/>
        <KV title="Plano por aluno (dedupe CPF)" data={{ ...s.plano_por_aluno, ...(Object.keys(s.outra_unidade_por_unidade || {}).length ? { "já em outra unidade": s.outra_unidade_por_unidade } : {}) }} testid="sum-plano-aluno"/>
        <KV title="Decisão por situação do contrato" data={s.plano_por_situacao} testid="sum-por-situacao"/>
      </div>
      <PendingSamples samples={pv.action_samples}/>
    </div>
  );
};
